import React from 'react';

interface TestimonialCardProps {
  name: string;
  designation?: string;
  organization?: string;
  role?: string; // Keep for backward compatibility
  image?: string; // Make optional since we'll have fallback
  content: string; // Will support HTML
  gradient: string;
}

// Function to get initials from name
function getInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .slice(0, 2) // Take only first 2 initials
    .join('');
}

// Function to generate a consistent color based on name
function getInitialsColor(name: string): string {
  const colors = [
    'bg-gradient-to-br from-blue-500 to-blue-600',
    'bg-gradient-to-br from-emerald-500 to-emerald-600',
    'bg-gradient-to-br from-purple-500 to-purple-600',
    'bg-gradient-to-br from-orange-500 to-orange-600',
    'bg-gradient-to-br from-rose-500 to-rose-600',
    'bg-gradient-to-br from-teal-500 to-teal-600',
    'bg-gradient-to-br from-indigo-500 to-indigo-600',
    'bg-gradient-to-br from-pink-500 to-pink-600',
  ];
  
  // Use name length and first character to pick a consistent color
  const index = (name.length + name.charCodeAt(0)) % colors.length;
  return colors[index];
}

export function TestimonialCard({ 
  name, 
  designation, 
  organization, 
  role, // Keep for backward compatibility
  image, 
  content, 
  gradient 
}: TestimonialCardProps) {
  // Determine the role/position text - prioritize new structure
  const positionText = designation && organization 
    ? `${designation} at ${organization}`
    : role || `${designation || 'Professional'} ${organization ? `at ${organization}` : ''}`;

  const initials = getInitials(name);
  const initialsColor = getInitialsColor(name);

  return (
    <div className="group relative rounded-2xl p-1.5 overflow-visible will-change-transform transform-gpu testimonial-card">
      {/* Enhanced glow effect - higher opacity and more intense */}
      <div className="absolute -inset-3 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-35 blur-xl transition-opacity duration-200 ease-out" />
      
      {/* Glassy border with flowing colors */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-background-light/50 to-surface-light/50 dark:from-background-dark/50 dark:to-surface-dark/50 backdrop-blur-sm border border-white/20 dark:border-white/10">
        {/* Animated gradient border - enhanced opacity */}
        <div className="absolute inset-0 rounded-2xl overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-out will-change-opacity">
          <div 
            className="absolute -inset-[200%] animate-spin-clockwise will-change-transform"
            style={{
              background: `conic-gradient(from 0deg at 50% 50%,
                rgba(37,99,235,0.25) 0deg,
                rgba(16,185,129,0.25) 120deg,
                rgba(37,99,235,0.25) 240deg,
                rgba(37,99,235,0.25) 360deg
              )`,
              transformOrigin: 'center',
              backfaceVisibility: 'hidden'
            }}
          />
        </div>
      </div>
      
      {/* Glass container with dynamic height */}
      <div 
        className={`relative p-6 rounded-xl bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md shadow-[0_8px_32px_rgba(37,99,235,0.15)] dark:shadow-[0_8px_32px_rgba(59,130,246,0.15)] ${content.length <= 200 ? 'testimonial-card-short' : content.length <= 300 ? 'testimonial-card-medium' : content.length <= 400 ? 'testimonial-card-long' : 'testimonial-card-extra-long'}`}
      >
        {/* Optimized floating particles */}
        <div className="absolute inset-0 overflow-hidden rounded-xl">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-primary-light/20 dark:bg-primary-dark/20 animate-float will-change-transform"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden'
              }}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative h-full flex flex-col">
          {/* Testimonial text with HTML support */}
          <div 
            className="text-text-secondary-light dark:text-text-secondary-dark mb-6 flex-grow group-hover:text-text-primary-light dark:group-hover:text-text-primary-dark transition-colors duration-300"
            dangerouslySetInnerHTML={{ 
              __html: content.startsWith('"') ? content : `"${content}"` 
            }}
          />

          {/* Author */}
          <div className="flex items-center gap-4">
            {/* Profile picture or initials fallback */}
            <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/20 dark:ring-white/10 group-hover:ring-primary-light/30 dark:group-hover:ring-primary-dark/30 transition-colors duration-200 ease-out flex-shrink-0">
              {image ? (
                <img 
                  src={image} 
                  alt={name}
                  className="w-full h-full object-cover transform transition-transform duration-500 ease-out group-hover:scale-110 will-change-transform"
                  style={{ 
                    backfaceVisibility: 'hidden',
                    transform: 'translateZ(0) scale(1)',
                    aspectRatio: '1 / 1'
                  }}
                  onError={(e) => {
                    // If image fails to load, hide it and show initials fallback
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="w-full h-full ${initialsColor} flex items-center justify-center text-white font-semibold text-base transform transition-transform duration-500 ease-out group-hover:scale-110 will-change-transform" style="backface-visibility: hidden; transform: translateZ(0) scale(1); font-family: 'Space Grotesk', sans-serif; aspect-ratio: 1 / 1;">
                          ${initials}
                        </div>
                      `;
                    }
                  }}
                />
              ) : (
                <div 
                  className={`w-full h-full ${initialsColor} flex items-center justify-center text-white font-semibold text-base transform transition-transform duration-500 ease-out group-hover:scale-110 will-change-transform`}
                  style={{ 
                    backfaceVisibility: 'hidden',
                    transform: 'translateZ(0) scale(1)',
                    fontFamily: "'Space Grotesk', sans-serif",
                    aspectRatio: '1 / 1'
                  }}
                >
                  {initials}
                </div>
              )}
            </div>
            <div>
              <h4 
                className="font-semibold text-text-primary-light dark:text-text-primary-dark transform transition-transform duration-200 ease-out group-hover:translate-x-1 will-change-transform text-base"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {name}
              </h4>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark transform transition-transform duration-200 ease-out group-hover:translate-x-1 will-change-transform">
                {positionText}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}